import classNames from 'classnames';
import { createRef, useEffect, useState } from 'react';
import { ScrapCloseForm, ScrapReason, ScrapReasons } from '../../shared/types/operation.ts';
import { Operation, OperationWithLabel } from '../types/sharedTypeImpl.ts';
import { trpc } from '../utils/trpc/trpc.ts';
import { Input } from './input.tsx';
import { ModalDialog } from './modalDialog.tsx';
import { ModalSubmitDialog } from './modalSubmitDialog.tsx';
import { OperatorSelect } from './operatorSelect.tsx';
import { ProtocolPreviewDialog } from './protocol-preview-dialog/protocolPreviewDialog.tsx';

interface Props {
    isOpen: boolean;
    operation: OperationWithLabel;
    onClose: () => void;
    onSuccess: (operation: Operation) => void;
}

export function ScrapCloseDialog({ isOpen, operation, onClose, onSuccess }: Props) {
    const [confirmedScrap, setConfirmedScrap] = useState('0');
    const [scrapReason, setScrapReason] = useState<ScrapReason | null>(null);
    const [comment, setComment] = useState('');
    const [timeSpentHours, setTimeSpentHours] = useState<number | null>(null);
    const [timeSpentMinutes, setTimeSpentMinutes] = useState<number | null>(null);
    const [operator, setOperator] = useState('');
    const [showPdfDialog, setShowPdfDialog] = useState(false);
    const [showOverLimitWarning, setShowOverLimitWarning] = useState(false);
    const formRef = createRef<HTMLFormElement>();

    const operationUpdate = trpc.operations.update.useMutation();

    // Set initial values from operation, if editing existing scrap close form
    useEffect(() => {
        if (operation.scrapCloseForm && isOpen) {
            setConfirmedScrap(operation.scrapCloseForm.addedConfirmedScrap.toString());
            setScrapReason(operation.scrapCloseForm.scrapReason ?? null);
            setComment(operation.scrapCloseForm.comment);
            if (operation.scrapCloseForm.timeSpent != null) {
                setTimeSpentHours(Math.floor(operation.scrapCloseForm.timeSpent / 60_000 / 60));
                setTimeSpentMinutes(Math.floor((operation.scrapCloseForm.timeSpent / 60_000) % 60));
            }
            setOperator(operation.scrapCloseForm.operator);
        }
    }, [operation, isOpen]);

    const handleConfirmAll = (e: React.MouseEvent) => {
        e.preventDefault();
        setConfirmedScrap(String(Math.max(0, (operation.potentialScraps ?? 0) - (operation.confirmedScraps ?? 0))));
    };

    const handleScrapReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setScrapReason(e.target.value as ScrapReason);
    };

    const handleTimeSpentHoursChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTimeSpentHours(parseInt(e.target.value));
        if (timeSpentMinutes === null) {
            setTimeSpentMinutes(0);
        }
    };

    const handleTimeSpentMinutesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTimeSpentMinutes(parseInt(e.target.value));
        if (timeSpentHours === null) {
            setTimeSpentHours(0);
        }
    };

    const submitOperation = async (operationWithScrapClose: OperationWithLabel) => {
        const { operation } = await operationUpdate.mutateAsync({
            operation: operationWithScrapClose,
            releaseScrapLabel: true,
        });

        onSuccess(operation);
        onClose();
    };

    const handleSubmit = async () => {
        const operationWithScrapClose = validateWarnAndGetOp();
        if (!operationWithScrapClose) {
            return;
        }

        if (operationWithScrapClose.scrapCloseForm.addedConfirmedScrap > maxWithoutWarning) {
            setShowOverLimitWarning(true);
            return;
        }

        await submitOperation(operationWithScrapClose);
    };

    const handleWarningSubmit = async () => {
        const operationWithScrapClose = validateWarnAndGetOp();
        if (!operationWithScrapClose) {
            setShowOverLimitWarning(false);
            return;
        }

        setShowOverLimitWarning(false);
        await submitOperation(operationWithScrapClose);
    };

    const validateWarnAndGetOp = () => {
        const isValidAuto = formRef.current?.reportValidity();
        if (!isValidAuto) {
            return null;
        }

        const confirmedScrapInt = parseInt(confirmedScrap);

        const scrapCloseForm: ScrapCloseForm = {
            addedConfirmedScrap: confirmedScrapInt,
            ...(confirmedScrapInt && { scrapReason: scrapReason ?? undefined }),
            comment,
            ...(timeSpentHours != null && timeSpentMinutes != null && { timeSpent: (timeSpentHours * 60 + timeSpentMinutes) * 60_000 }),
            operator,
            time: operation?.scrapCloseForm?.time ?? undefined,
        };
        const operationWithScrapClose = {
            ...operation,
            scrapCloseForm: scrapCloseForm as ScrapCloseForm,
        };
        delete operationWithScrapClose.scrapLabel;

        return operationWithScrapClose;
    };

    const operationDetails = operation.details.join(' > ');
    const maxWithoutWarning = Math.max(0, (operation.partCount ?? 0) - (operation.confirmedScraps ?? 0));
    const confirmedScrapInt = parseInt(confirmedScrap);
    const isOverLimitWarningVisible = !isNaN(confirmedScrapInt) && confirmedScrapInt > maxWithoutWarning;
    const isReasonDisabled = !confirmedScrap || !parseInt(confirmedScrap);
    const isReasonRequired = !!confirmedScrap && parseInt(confirmedScrap) > 0;
    return (
        <>
            <ModalSubmitDialog
                isOpen={isOpen}
                isSubmitting={operationUpdate.isPending}
                frameClassName='modal-frame--operation-dialog'
                contentClassName='modal-content--tablet'
                onSubmit={handleSubmit}
                onCancel={onClose}
            >
                <div className='operation-dialog__part_number'>{operation.partDisplayName}</div>
                <button style={{ float: 'right' }} onClick={() => setShowPdfDialog(true)}>
                    View pdf
                </button>
                <div style={{ color: '#333' }}>
                    <div>{operationDetails}</div>
                    <div>Initial confirmed scraps {operation.confirmedScraps ?? 0}</div>
                </div>

                <form ref={formRef} style={{ marginTop: '20px' }}>
                    <div className='dialog-form__field--with-margins'>
                        <div>Additional confirmed scrap (potential: {operation.potentialScraps})</div>
                        <div className='dialog-form__input' style={{ display: 'flex', flexDirection: 'row' }}>
                            <Input
                                style={{ width: '100%' }}
                                type='number'
                                step={1}
                                min={0}
                                required
                                autoComplete='off'
                                value={confirmedScrap}
                                onChange={(e) => setConfirmedScrap(e.target.value)}
                            />
                            <button style={{ paddingLeft: '16px', paddingRight: '16px' }} onClick={handleConfirmAll}>
                                All
                            </button>
                        </div>
                        {isOverLimitWarningVisible && (
                            <div style={{ marginTop: '8px', color: '#9a6700' }}>
                                Warning: {confirmedScrapInt} exceeds the current part count-based limit of {maxWithoutWarning}. You can still submit
                                after confirming that this is intentional.
                            </div>
                        )}
                    </div>

                    <fieldset className={classNames('dialog-form__fieldset', { 'text--disabled': isReasonDisabled })} disabled={isReasonDisabled}>
                        <legend>Scrap reason</legend>
                        {ScrapReasons.map(
                            (scrapReasonOption) =>
                                (!scrapReasonOption.isHidden || scrapReasonOption.label === operation.scrapCloseForm?.scrapReason) && (
                                    <RadioButton
                                        key={scrapReasonOption.label}
                                        value={scrapReasonOption.label}
                                        checked={scrapReasonOption.label === scrapReason}
                                        onChange={handleScrapReasonChange}
                                        required={isReasonRequired}
                                    />
                                ),
                        )}
                    </fieldset>

                    <div className='dialog-form__field--with-margins'>
                        <div>Comment</div>
                        <textarea
                            className='dialog-form__input'
                            value={comment}
                            rows={2}
                            autoComplete='off'
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <div className='dialog-form__field--with-margins'>
                        <div>Time spent</div>
                        <div className='dialog-form__input' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Select options={[0, 1, 2, 3, 4, 5]} value={timeSpentHours} onChange={handleTimeSpentHoursChange} />
                            <span style={{ fontSize: 'larger', marginLeft: '4px', marginRight: '12px' }}>h</span>

                            <Select options={[0, 10, 20, 30, 40, 50]} value={timeSpentMinutes} onChange={handleTimeSpentMinutesChange} />
                            <span style={{ fontSize: 'larger', marginLeft: '4px', marginRight: '12px' }}>m</span>
                        </div>
                    </div>

                    <div className='dialog-form__field--with-margins'>
                        <div>Operator</div>
                        <OperatorSelect
                            className='dialog-form__input'
                            required={true}
                            operatorInitials={operator}
                            onChange={(operatorInitials) => setOperator(operatorInitials)}
                        />
                    </div>
                </form>
            </ModalSubmitDialog>

            <ProtocolPreviewDialog
                product={operation.product}
                initialPositionIdx={null}
                show={showPdfDialog}
                onClose={() => setShowPdfDialog(false)}
            />

            <ModalDialog isOpen={showOverLimitWarning} frameClassName='modal-frame--fixed_width' onCancel={() => setShowOverLimitWarning(false)}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '12px' }}>Confirm scrap count</div>
                <div>
                    You are adding {isNaN(confirmedScrapInt) ? confirmedScrap : confirmedScrapInt} scraps, which exceeds the current part count-based
                    limit of {maxWithoutWarning}.
                </div>
                <div style={{ marginTop: '8px', color: '#555' }}>
                    This can happen for example when the part count has reset to zero for some reason.
                </div>
                <div className='dialog--button-bar'>
                    <button onClick={() => setShowOverLimitWarning(false)}>Cancel</button>
                    <button onClick={handleWarningSubmit} disabled={operationUpdate.isPending}>
                        {operationUpdate.isPending ? 'Submitting...' : 'Continue'}
                    </button>
                </div>
            </ModalDialog>
        </>
    );
}

function RadioButton({
    value,
    checked,
    required,
    onChange,
}: {
    value: string,
    checked: boolean,
    required: boolean,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
}) {
    return (
        <label>
            <input
                className='radio--vert'
                type='radio'
                name='scrapReason'
                required={required}
                value={value}
                checked={checked}
                onChange={onChange}
            />
            {value}
        </label>
    )
}

function Select({
    value,
    options,
    onChange,
}: {
    value: number | null,
    options: number[],
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
}) {
    return (
        <select
            style={{ width: '70px' }}
            value={value ?? ''}
            required
            onChange={onChange}
        >
            <option value={''} disabled hidden />
            {options.map(option =>
                <option key={option} value={option}>{option}</option>
            )}
        </select>
    )
}
